/* ==========================================================================
   EMWC before / after image comparison

   Web component, matching Impact's architecture. Custom elements upgrade
   automatically when inserted into the DOM, so this re-initialises correctly
   when the theme editor re-renders the section.

   WHY POINTER EVENTS AND NOT THE NATIVE RANGE DRAG
   ------------------------------------------------
   Browsers disagree about range inputs. Android Chrome moves the thumb to
   wherever you tap on the track and lets you drag from there. iOS Safari does
   not: the drag must begin on the thumb itself. Because the thumb here is
   invisible and 2px wide, that made the slider effectively undraggable on
   iPhone while working fine on Android.

   So all pointer interaction is handled here on the host element, and the
   range input is set to pointer-events: none. The input stays in the DOM as
   the accessible control: it is still focusable, arrow keys still work, and
   screen readers still announce it. CSS sets touch-action: pan-y on the host
   so vertical page scrolling is unaffected by a horizontal drag.
   ========================================================================== */
(function () {
  'use strict';
  if (customElements.get('emwc-compare')) return;

  class EmwcCompare extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('[data-emwc-compare-frame]');
      this.input = this.querySelector('[data-emwc-compare-range]');
      if (!this.frame) return;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onInput = this.onInput.bind(this);

      this.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('pointermove', this.onPointerMove);
      this.addEventListener('pointerup', this.onPointerUp);
      this.addEventListener('pointercancel', this.onPointerUp);

      if (this.input) {
        this.input.addEventListener('input', this.onInput);
        this.apply(this.input.value);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('pointerdown', this.onPointerDown);
      this.removeEventListener('pointermove', this.onPointerMove);
      this.removeEventListener('pointerup', this.onPointerUp);
      this.removeEventListener('pointercancel', this.onPointerUp);
      if (this.input) this.input.removeEventListener('input', this.onInput);
    }

    onPointerDown(event) {
      if (event.button && event.button !== 0) return;
      this.dragging = true;
      this.setAttribute('data-dragging', '');
      if (this.setPointerCapture) {
        try { this.setPointerCapture(event.pointerId); } catch (e) { /* no-op */ }
      }
      this.setFromClientX(event.clientX);
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      this.setFromClientX(event.clientX);
    }

    onPointerUp(event) {
      this.dragging = false;
      this.removeAttribute('data-dragging');
      if (this.releasePointerCapture) {
        try { this.releasePointerCapture(event.pointerId); } catch (e) { /* no-op */ }
      }
    }

    onInput(event) {
      this.apply(event.target.value);
    }

    setFromClientX(clientX) {
      var rect = this.frame.getBoundingClientRect();
      if (!rect.width) return;
      var pct = ((clientX - rect.left) / rect.width) * 100;
      pct = Math.min(100, Math.max(0, pct));
      this.apply(pct);
      if (this.input) this.input.value = Math.round(pct);
    }

    apply(value) {
      var pct = Number(value);
      if (isNaN(pct)) return;
      pct = Math.min(100, Math.max(0, pct));
      this.style.setProperty('--emwc-compare-pos', pct + '%');
      if (this.input) this.input.setAttribute('aria-valuetext', Math.round(pct) + '% before');
    }
  }

  customElements.define('emwc-compare', EmwcCompare);
})();
